import { t as localeText } from "@/shared/lib/translate";
import { BoundingBoxIcon } from "@phosphor-icons/react/dist/csr/BoundingBox";
import { GridFourIcon } from "@phosphor-icons/react/dist/csr/GridFour";
import { HeartIcon, HeartOutlineIcon, ThreeDIcon, ImageIcon, VideoIcon, AudioIcon, SearchIcon } from "./icons";
import { ImageSquareIcon } from "@phosphor-icons/react/dist/csr/ImageSquare";
import { ListDashesIcon } from "@phosphor-icons/react/dist/csr/ListDashes";
import { MagnifyingGlassIcon } from "@phosphor-icons/react/dist/csr/MagnifyingGlass";
import { VideoCameraIcon } from "@phosphor-icons/react/dist/csr/VideoCamera";
import { WaveformIcon } from "@phosphor-icons/react/dist/csr/Waveform";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  CREATION_MEDIA_TYPES,
  type CreationFilterControls,
  type CreationMediaType,
  type CreationView,
} from "@/shared/lib/creationFilters";
import { Button } from "./Button";
import { Checkbox } from "./Choices";
import { IconButton } from "./IconButton";
import { Popover } from "./Popover";
import { SegmentedControl } from "./SegmentedControl";
import { Text } from "./Text";
import { SearchField } from "./Field";
import { AssetHistoryFilters } from "./AssetHistoryFilters";
import type { AssetBrowseCandidate } from "@/shared/lib/assetBrowseFilters";

export interface CreationsToolbarProps extends CreationFilterControls {
  /** Optional leading status (take counts and similar workflow badges). */
  status?: ReactNode;
  hideType?: boolean;
  /** Simplified controls for the docked image, video and 3D workspaces. */
  compactHistory?: boolean;
  hideFavorites?: boolean;
  filtersControl?: ReactNode;
  assets?: AssetBrowseCandidate[];
}

/**
 * The one toolbar every creation container uses. Switching workflow only
 * re-parameterises it — the controls, their order, and their sizes never move.
 */
export function CreationsToolbar({
  filters,
  toggleMedia,
  setFavoritesOnly,
  setView,
  setSearch,
  setBrowse,
  status,
  hideType = false,
  compactHistory = false,
  hideFavorites = false,
  filtersControl,
  assets = [],
}: CreationsToolbarProps) {
  const { t } = useTranslation();

  return (
    <div
      role="toolbar"
      aria-label={t("generate.result.creationTools", {
        defaultValue: "Creation tools",
      })}
      className={`${compactHistory ? "workspace-history-toolbar " : ""}flex max-w-full flex-wrap items-center justify-end gap-2`}
    >
      {status}
      {!hideType ? <Popover
        label={t("generate.result.creationType", {
          defaultValue: "Creation type",
        })}
        align="end"
        panelClassName="w-[min(240px,calc(100vw-24px))] p-3"
        trigger={({ toggle, open }) => (
          <Button
            variant="secondary"
            aria-haspopup="dialog"
            aria-expanded={open}
            aria-label={`${t("generate.result.creationType", {
              defaultValue: "Creation type",
            })}: ${creationTypeSummary(t, filters.media)}`}
            onClick={toggle}
            className="min-w-[148px] justify-between"
          >
            <span className="flex min-w-0 items-center gap-1.5">
              {filters.media.map((media) => (
                <CreationTypeIcon key={media} type={media} unified={compactHistory} />
              ))}
              <span className="min-w-0 truncate">
                {creationTypeSummary(t, filters.media)}
              </span>
            </span>
          </Button>
        )}
      >
        {() => (
          <div className="grid gap-0">
            <Text
              variant="ui"
              as="p"
              className="mb-1 text-[11px] font-semibold uppercase tracking-[0.07em] text-ink-faint"
            >
              {t("generate.result.creationType", {
                defaultValue: "Creation type",
              })}
            </Text>
            {CREATION_MEDIA_TYPES.map((media) => (
              <Checkbox
                key={media}
                className="sm:min-h-8 sm:py-1"
                checked={filters.media.includes(media)}
                onChange={() => toggleMedia(media)}
                label={<span className="flex items-center gap-2">{compactHistory ? <CreationTypeIcon type={media} unified /> : null}{creationTypeLabel(t, media)}</span>}
              />
            ))}
          </div>
        )}
      </Popover> : null}
      {!hideFavorites ? <FavoritesOnlyButton
        active={filters.favoritesOnly}
        onChange={setFavoritesOnly}
      /> : null}
      {!compactHistory ? <CreationLayoutButton view={filters.view} onChange={setView} /> : null}
      {filtersControl ?? <AssetHistoryFilters
        filters={filters.browse}
        assets={assets}
        favoritesOnly={filters.favoritesOnly}
        onChange={setBrowse}
        onFavoritesChange={setFavoritesOnly}
      />}
      <AssetSearchButton search={filters.search} onSearchChange={setSearch} unified={compactHistory} />
    </div>
  );
}

export function FavoritesOnlyButton({
  active,
  onChange,
  label,
}: {
  active: boolean;
  onChange: (value: boolean) => void;
  label?: string;
}) {
  const { t } = useTranslation();
  const accessibleLabel =
    label ??
    t("generate.result.favoritesOnly", {
      defaultValue: "Show favorites only",
    });
  return (
    <IconButton
      tone="soft"
      className="studio-favorite"
      tooltip={accessibleLabel}
      aria-label={accessibleLabel}
      aria-pressed={active}
      onClick={() => onChange(!active)}
    >
      {active ? <HeartIcon aria-hidden="true" focusable="false" /> : <HeartOutlineIcon aria-hidden="true" focusable="false" />}
    </IconButton>
  );
}

export function AssetSearchButton({
  search,
  onSearchChange,
  label,
  placeholder,
  unified = false,
}: {
  search: string;
  onSearchChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  unified?: boolean;
}) {
  const { t } = useTranslation();
  const accessibleLabel =
    label ??
    t("generate.result.searchCreations", {
      defaultValue: "Search creations",
    });
  return (
    <Popover
      label={accessibleLabel}
      align="end"
      panelClassName="studio-search-popover w-[min(340px,calc(100vw-24px))] p-0"
      trigger={({ toggle, open }) => (
        <IconButton
          aria-expanded={open}
          data-active={unified && (open || Boolean(search)) || undefined}
          tone={search ? "glass" : "soft"}
          tooltip={accessibleLabel}
          aria-label={accessibleLabel}
          aria-haspopup="dialog"
          onClick={toggle}
        >
          {unified ? <SearchIcon /> : <MagnifyingGlassIcon aria-hidden="true" weight="bold" className="size-[18px]" />}
        </IconButton>
      )}
    >
      {() => (
        <SearchField label={accessibleLabel} value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          onClear={() => onSearchChange("")}
          placeholder={placeholder ?? t("generate.result.searchPlaceholder", { defaultValue: "Search creations…" })} />
      )}
    </Popover>
  );
}

type Translate = (key: string, options?: { defaultValue?: string }) => string;

function creationTypeLabel(t: Translate, media: CreationMediaType): string {
  const fallback =
    media === "image" ? localeText("interface.image_50e19fda", { defaultValue: "Image" }) : media === "video" ? localeText("interface.video_bc17c1f0", { defaultValue: "Video" }) : media === "audio" ? localeText("interface.audio_acdac205", { defaultValue: "Audio" }) : "3D";
  return t(`generate.result.creationTypeOption.${media}`, {
    defaultValue: fallback,
  });
}

function creationTypeSummary(
  t: Translate,
  media: readonly CreationMediaType[],
): string {
  if (media.length === CREATION_MEDIA_TYPES.length) {
    return t("generate.result.creationTypeOption.all", {
      defaultValue: "All types",
    });
  }
  return media.map((type) => creationTypeLabel(t, type)).join(", ");
}

function CreationTypeIcon({ type, unified = false }: { type: CreationMediaType; unified?: boolean }) {
  if (unified) {
    const Icon = type === "image" ? ImageIcon : type === "video" ? VideoIcon : type === "audio" ? AudioIcon : ThreeDIcon;
    return <Icon className="size-4 shrink-0" />;
  }
  const props = {
    "aria-hidden": true,
    focusable: "false" as const,
    weight: "bold" as const,
    className: "size-4 shrink-0",
  };
  if (type === "video") return <VideoCameraIcon {...props} />;
  if (type === "audio") return <WaveformIcon {...props} />;
  if (type === "3d") return <ThreeDIcon {...props} />;
  return <ImageSquareIcon {...props} />;
}

function LayoutIcon({ view }: { view: CreationView }) {
  const props = {
    "aria-hidden": true,
    focusable: "false" as const,
    weight: "bold" as const,
  };
  if (view === "row") return <ListDashesIcon {...props} />;
  if (view === "square") return <GridFourIcon {...props} />;
  return <BoundingBoxIcon {...props} />;
}

export function CreationLayoutButton({ view, onChange }: { view: CreationView; onChange: (view: CreationView) => void }) {
  const { t } = useTranslation();
  return (
      <Popover
        label={t("generate.result.creationLayout", {
          defaultValue: "Creations layout",
        })}
        align="end"
        panelClassName="w-[min(360px,calc(100vw-24px))] p-4"
        trigger={({ toggle }) => (
          <IconButton
            tone="soft"
            tooltip={t("generate.result.chooseCreationLayout", {
              defaultValue: "Choose creations layout",
            })}
            aria-label={t("generate.result.chooseCreationLayout", {
              defaultValue: "Choose creations layout",
            })}
            aria-haspopup="dialog"
            onClick={toggle}
          >
            <LayoutIcon view={view} />
          </IconButton>
        )}
      >
        {() => (
          <div>
            <Text variant="ui" as="p" className="mb-3 text-[13px]">
              {t("generate.result.creationLayout", {
                defaultValue: "Creations layout",
              })}
            </Text>
            <SegmentedControl
              aria-label={t("generate.result.creationLayout", {
                defaultValue: "Creations layout",
              })}
              value={view}
              onChange={(value) => onChange(value as CreationView)}
              options={([
                ["row", "Row"],
                ["original", "Original"],
                ["square", "Square"],
              ] as const).map(([value, defaultLabel]) => ({
                value,
                label: t(`generate.result.creationLayoutOption.${value}`, {
                  defaultValue: defaultLabel,
                }),
              }))}
            />
          </div>
        )}
      </Popover>
  );
}
